import * as React from 'react'
import { Search, Upload } from 'lucide-react'
import { useQueryDomains } from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/misc'
import { EmptyState, InlineSpinner } from '@/components/ui/states'
import { SimilarPanel } from '@/features/domain/SimilarPanel'
import { FeatureTags, StatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { DomainDetailDrawer } from '@/components/layout/DomainDetailDrawer'
import { formatNumber } from '@/lib/utils'

const MAX_BATCH = 500

export default function DomainQueryPage() {
  const [input, setInput] = React.useState('')
  const [resolve, setResolve] = React.useState(false)
  const [detailId, setDetailId] = React.useState<string | null>(null)
  const query = useQueryDomains()
  const toast = useToast()

  const lines = input.split('\n').map((s) => s.trim()).filter(Boolean)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (lines.length === 0) return
    if (lines.length > MAX_BATCH) {
      toast.warning('超出批量上限', `单次最多查询 ${MAX_BATCH} 个域名`)
      return
    }
    try {
      const res = await query.mutateAsync({ domains: lines, resolve })
      if (res.confirmations_queued > 0) {
        toast.success(
          `已排入 ${res.confirmations_queued} 次外部确认`,
          '确认完成后状态会自动更新；缓存命中不消耗额度',
        )
      }
    } catch (err) {
      toast.error('查询失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setInput(String(reader.result ?? ''))
    reader.readAsText(file)
  }

  return (
    <>
      <PageHeader
        title="域名查询"
        description="查询前会做 IDNA 规范化与去重；未确认状态不代表可注册。"
      />

      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>输入域名</CardTitle>
            <CardDescription>每行一个，最多 {MAX_BATCH} 个</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={12}
                className="font-mono text-xs"
                placeholder={'example.com\n例え.jp\nhttps://foo.xyz/path'}
                aria-label="待查询域名"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{lines.length} 行</span>
                <label className="inline-flex cursor-pointer items-center gap-1.5 hover:text-foreground">
                  <Upload className="h-3.5 w-3.5" aria-hidden />
                  上传文件
                  <input type="file" accept=".txt,.csv" className="sr-only" onChange={onFile} />
                </label>
              </div>

              <label className="flex items-start gap-2 rounded-md border p-2.5 text-xs">
                <Checkbox checked={resolve} onCheckedChange={(v) => setResolve(Boolean(v))} />
                <span>
                  <span className="font-medium">同时请求外部确认</span>
                  <span className="mt-0.5 block text-muted-foreground">
                    仅对缓存未命中的域名发起实际外部调用，并扣除 external_query 额度；
                    缓存命中与并发共享查询不重复计费。
                  </span>
                </span>
              </label>

              <Button type="submit" className="w-full" disabled={query.isPending || lines.length === 0}>
                {query.isPending ? <InlineSpinner label="查询中" /> : <><Search className="h-4 w-4" aria-hidden /> 查询</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>查询结果</CardTitle>
            {query.data && (
              <CardDescription>
                新入库 {formatNumber(query.data.created)} 个 ·
                已排入确认 {formatNumber(query.data.confirmations_queued)} 次
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {!query.data ? (
              <EmptyState title="尚无查询结果" description="在左侧输入域名后开始查询。" icon={Search} />
            ) : (
              <>
                <p className="mb-3 rounded-md bg-muted p-2.5 text-xs text-muted-foreground">
                  {query.data.notice}
                </p>
                <div className="scroll-x">
                  <table className="w-full min-w-[620px] text-sm">
                    <thead className="border-b">
                      <tr>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">输入</th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">规范化</th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">状态</th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">特征</th>
                      </tr>
                    </thead>
                    <tbody>
                      {query.data.results.map((r) => (
                        <tr key={r.input} className="border-b last:border-0">
                          <td className="px-2 py-2 font-mono text-xs text-muted-foreground">{r.input}</td>
                          <td className="px-2 py-2">
                            {r.error ? (
                              <span className="text-xs text-destructive">{r.error}</span>
                            ) : r.record ? (
                              <button
                                onClick={() => setDetailId(r.record!.id)}
                                className="font-mono text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                {r.domain}
                              </button>
                            ) : (
                              <span className="font-mono text-xs">{r.domain}</span>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            {r.record ? (
                              <div className="flex flex-wrap items-center gap-1">
                                <StatusBadge
                                  status={r.record.current_status}
                                  source={r.record.status_source}
                                  confirmedAt={r.record.last_resolved_at}
                                  confidence={r.record.status_confidence}
                                />
                                {r.confirmation_queued && <Badge variant="warning">确认中</Badge>}
                                {r.known && r.status_fresh && <Badge variant="muted">缓存</Badge>}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <FeatureTags tags={r.record?.feature_tags ?? r.features} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 rounded-lg border bg-card">
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-medium">相似域名推荐</h2>
          <p className="text-xs text-muted-foreground">
            按可解释的规则生成变体：换后缀、增删改字符、连字符、拼音音节、声母缩写、数字谐音。
            只查平台已收录的情况，不消耗外部查询额度。
          </p>
        </div>
        <div className="p-4">
          <SimilarPanel />
        </div>
      </div>

      <DomainDetailDrawer domainId={detailId} onClose={() => setDetailId(null)} />
    </>
  )
}
